import { useState, useEffect } from 'react';
import {
  Baby,
  Users,
  Activity,
  TrendingUp,
  Clock,
  Droplets,
  Moon,
  Pill,
  Thermometer,
  StickyNote,
  BarChart2,
  Calendar,
  PlayCircle,
  PauseCircle,
  Volume2,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';

// ============================================
// TYPES
// ============================================

interface KPIStats {
  totalEntries: number;
  activeUsers: number;
  entriesLast24h: number;
  entriesLast7d: number;
  feedingCount: number;
  diaperCount: number;
  sleepCount: number;
  medicationCount: number;
  temperatureCount: number;
  notesCount: number;
  avgEntriesPerUser: number;
}

interface MedPlanStats {
  totalReminders: number;
  activeReminders: number;
  pausedReminders: number;
  usersWithReminders: number;
  avgRemindersPerUser: number;
  topMedications: { name: string; count: number }[];
}

interface SleepPlanStats {
  totalActivePlans: number;
  usersWithPlans: number;
  avgPrematureWeeks: number;
}

interface SleepSoundsStats {
  totalSessions: number;
  totalPlayMinutes: number;
  activeSounds: number;
}

interface CareTypeStats {
  type: string;
  count: number;
  percentage: number;
}

type TimeRange = '24h' | '7d' | '30d' | 'all';

// ============================================
// KPI CARD COMPONENT
// ============================================

interface KPICardProps {
  title: string;
  value: string | number;
  icon: typeof Baby;
  color: string;
  trend?: number;
  subtitle?: string;
}

function KPICard({ title, value, icon: Icon, color, trend, subtitle }: KPICardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
          {trend !== undefined && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              <TrendingUp className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
              <span>{Math.abs(trend)}% vs. Vorperiode</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

// ============================================
// CARE TYPE BREAKDOWN COMPONENT
// ============================================

interface CareTypeBreakdownProps {
  stats: CareTypeStats[];
}

const careTypeInfo: Record<string, { label: string; icon: typeof Baby; color: string }> = {
  feeding: { label: 'Füttern', icon: Baby, color: 'bg-pink-100 text-pink-600' },
  diaper: { label: 'Windeln', icon: Droplets, color: 'bg-amber-100 text-amber-600' },
  sleep: { label: 'Schlafen', icon: Moon, color: 'bg-indigo-100 text-indigo-600' },
  medication: { label: 'Medikamente', icon: Pill, color: 'bg-red-100 text-red-600' },
  temperature: { label: 'Temperatur', icon: Thermometer, color: 'bg-emerald-100 text-emerald-600' },
  notes: { label: 'Notizen', icon: StickyNote, color: 'bg-blue-100 text-blue-600' },
};

function CareTypeBreakdown({ stats }: CareTypeBreakdownProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="font-semibold text-gray-900 mb-4">Einträge nach Typ</h3>
      <div className="space-y-3">
        {stats.map((stat) => {
          const info = careTypeInfo[stat.type] || { label: stat.type, icon: Activity, color: 'bg-gray-100 text-gray-600' };
          const Icon = info.icon;
          return (
            <div key={stat.type} className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${info.color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{info.label}</span>
                  <span className="text-sm text-gray-500">{stat.count}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-pink-500 rounded-full transition-all"
                    style={{ width: `${stat.percentage}%` }}
                  />
                </div>
              </div>
              <span className="text-xs text-gray-400 w-12 text-right">{stat.percentage.toFixed(1)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// MAIN PAGE COMPONENT
// ============================================

export function BabyCareAdminPage() {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [stats, setStats] = useState<KPIStats | null>(null);
  const [careTypeStats, setCareTypeStats] = useState<CareTypeStats[]>([]);
  const [medPlanStats, setMedPlanStats] = useState<MedPlanStats | null>(null);
  const [sleepPlanStats, setSleepPlanStats] = useState<SleepPlanStats | null>(null);
  const [sleepSoundsStats, setSleepSoundsStats] = useState<SleepSoundsStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchSubFeatureStats();
  }, [timeRange]);

  const getDateFilter = () => {
    const now = new Date();
    switch (timeRange) {
      case '24h':
        return new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      case '7d':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      case '30d':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      default:
        return null;
    }
  };

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const supabase = getSupabaseAdminClient();
      const dateFilter = getDateFilter();

      // Build base query
      let entriesQuery = supabase.from('baby_care_entries').select('id, care_type, pregnancy_id, created_at', { count: 'exact' });
      if (dateFilter) {
        entriesQuery = entriesQuery.gte('created_at', dateFilter);
      }
      const { data: entriesData, count: totalEntries } = await entriesQuery;
      const entries = entriesData as Array<{ id: string; care_type: string; pregnancy_id: string; created_at: string }> | null;

      // Get unique users
      const uniquePregnancies = new Set(entries?.map((e) => e.pregnancy_id) || []);
      const activeUsers = uniquePregnancies.size;

      // Count by type
      const typeCounts: Record<string, number> = {
        feeding: 0,
        diaper: 0,
        sleep: 0,
        medication: 0,
        temperature: 0,
        notes: 0,
      };

      entries?.forEach((e) => {
        if (typeCounts[e.care_type] !== undefined) {
          typeCounts[e.care_type]++;
        }
      });

      // Calculate care type stats with percentages
      const total = totalEntries || 1;
      const careStats: CareTypeStats[] = Object.entries(typeCounts).map(([type, count]) => ({
        type,
        count,
        percentage: (count / total) * 100,
      }));
      careStats.sort((a, b) => b.count - a.count);

      // Get entries from last 24h
      const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { count: entriesLast24h } = await supabase
        .from('baby_care_entries')
        .select('id', { count: 'exact' })
        .gte('created_at', last24h);

      // Get entries from last 7d
      const last7d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { count: entriesLast7d } = await supabase
        .from('baby_care_entries')
        .select('id', { count: 'exact' })
        .gte('created_at', last7d);

      const avgEntriesPerUser = activeUsers > 0 ? Math.round((totalEntries || 0) / activeUsers) : 0;

      setStats({
        totalEntries: totalEntries || 0,
        activeUsers,
        entriesLast24h: entriesLast24h || 0,
        entriesLast7d: entriesLast7d || 0,
        feedingCount: typeCounts.feeding,
        diaperCount: typeCounts.diaper,
        sleepCount: typeCounts.sleep,
        medicationCount: typeCounts.medication,
        temperatureCount: typeCounts.temperature,
        notesCount: typeCounts.notes,
        avgEntriesPerUser,
      });
      setCareTypeStats(careStats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubFeatureStats = async () => {
    try {
      const supabase = getSupabaseAdminClient();

      // Med-Plan Stats
      const { data: remindersData } = await supabase
        .from('medication_reminders')
        .select('id, pregnancy_id, medication_name, is_paused, is_active')
        .eq('is_active', true);

      const reminders = remindersData as Array<{ id: string; pregnancy_id: string; medication_name: string; is_paused: boolean; is_active: boolean }> | null;
      if (reminders) {
        const activeR = reminders.filter((r) => !r.is_paused);
        const pausedR = reminders.filter((r) => r.is_paused);
        const uniqueUsers = new Set(reminders.map((r) => r.pregnancy_id));

        // Top medications
        const medCounts: Record<string, number> = {};
        reminders.forEach((r) => {
          const name = r.medication_name.toLowerCase().trim();
          medCounts[name] = (medCounts[name] || 0) + 1;
        });
        const topMeds = Object.entries(medCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([name, count]) => ({ name, count }));

        setMedPlanStats({
          totalReminders: reminders.length,
          activeReminders: activeR.length,
          pausedReminders: pausedR.length,
          usersWithReminders: uniqueUsers.size,
          avgRemindersPerUser: uniqueUsers.size > 0 ? Math.round(reminders.length / uniqueUsers.size * 10) / 10 : 0,
          topMedications: topMeds,
        });
      }

      // Sleep Plan Stats
      const { data: sleepPlansData } = await supabase
        .from('sleep_plan_settings')
        .select('id, pregnancy_id, premature_weeks, is_active')
        .eq('is_active', true);

      const sleepPlans = sleepPlansData as Array<{ id: string; pregnancy_id: string; premature_weeks: number | null; is_active: boolean }> | null;
      if (sleepPlans) {
        const uniqueSleepUsers = new Set(sleepPlans.map((s) => s.pregnancy_id));
        const premWeeks = sleepPlans.filter((s) => s.premature_weeks && s.premature_weeks > 0);
        const avgPrem = premWeeks.length > 0
          ? Math.round(premWeeks.reduce((sum, s) => sum + (s.premature_weeks ?? 0), 0) / premWeeks.length * 10) / 10
          : 0;

        setSleepPlanStats({
          totalActivePlans: sleepPlans.length,
          usersWithPlans: uniqueSleepUsers.size,
          avgPrematureWeeks: avgPrem,
        });
      }

      // Sleep Sounds Stats
      const { data: sessionsData, count: totalSessions } = await supabase
        .from('sleep_sound_sessions')
        .select('actual_duration_seconds', { count: 'exact' });

      const { count: activeSounds } = await supabase
        .from('sleep_sounds')
        .select('id', { count: 'exact' })
        .eq('is_active', true);

      const sessions = sessionsData as Array<{ actual_duration_seconds: number | null }> | null;
      const totalPlaySeconds = sessions?.reduce((sum, s) => sum + (s.actual_duration_seconds ?? 0), 0) ?? 0;

      setSleepSoundsStats({
        totalSessions: totalSessions ?? 0,
        totalPlayMinutes: Math.round(totalPlaySeconds / 60),
        activeSounds: activeSounds ?? 0,
      });
    } catch (err) {
      console.error('Error fetching sub-feature stats:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Baby-Care-Koordinator</h1>
          <p className="text-gray-500 mt-1">Statistiken zur Baby-Pflege Nutzung</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1">
          {(['24h', '7d', '30d', 'all'] as TimeRange[]).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                timeRange === range
                  ? 'bg-pink-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {range === '24h' && '24 Stunden'}
              {range === '7d' && '7 Tage'}
              {range === '30d' && '30 Tage'}
              {range === 'all' && 'Gesamt'}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-16" />
            </div>
          ))}
        </div>
      ) : stats ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="Einträge gesamt"
              value={stats.totalEntries}
              icon={Activity}
              color="bg-pink-100 text-pink-600"
            />
            <KPICard
              title="Aktive Nutzer"
              value={stats.activeUsers}
              icon={Users}
              color="bg-purple-100 text-purple-600"
              subtitle="Mit Einträgen"
            />
            <KPICard
              title="Letzte 24h"
              value={stats.entriesLast24h}
              icon={Clock}
              color="bg-blue-100 text-blue-600"
              subtitle="Neue Einträge"
            />
            <KPICard
              title="Ø pro Nutzer"
              value={stats.avgEntriesPerUser}
              icon={BarChart2}
              color="bg-teal-100 text-teal-600"
              subtitle="Einträge"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Care Type Breakdown */}
            <CareTypeBreakdown stats={careTypeStats} />

            {/* Type Quick Stats */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Schnellübersicht</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-pink-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Baby className="w-5 h-5 text-pink-600" />
                    <span className="text-sm font-medium text-pink-900">Fütterungen</span>
                  </div>
                  <p className="text-2xl font-bold text-pink-700">{stats.feedingCount}</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Droplets className="w-5 h-5 text-amber-600" />
                    <span className="text-sm font-medium text-amber-900">Windeln</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-700">{stats.diaperCount}</p>
                </div>
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Moon className="w-5 h-5 text-indigo-600" />
                    <span className="text-sm font-medium text-indigo-900">Schlaf</span>
                  </div>
                  <p className="text-2xl font-bold text-indigo-700">{stats.sleepCount}</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Thermometer className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-900">Temperatur</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-700">{stats.temperatureCount}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-gray-400" />
              <h3 className="font-semibold text-gray-900">Aktivität</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">{stats.entriesLast24h}</p>
                <p className="text-sm text-gray-500 mt-1">Einträge heute</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">{stats.entriesLast7d}</p>
                <p className="text-sm text-gray-500 mt-1">Einträge diese Woche</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">{stats.totalEntries}</p>
                <p className="text-sm text-gray-500 mt-1">Einträge gesamt</p>
              </div>
            </div>
          </div>

          {/* Med-Plan Section */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Pill className="w-5 h-5 text-red-500" />
              <h3 className="font-semibold text-gray-900">Med-Plan</h3>
            </div>
            {medPlanStats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-red-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <PlayCircle className="w-4 h-4 text-red-500" />
                      <span className="text-xs font-medium text-red-800">Aktive Pläne</span>
                    </div>
                    <p className="text-2xl font-bold text-red-700">{medPlanStats.activeReminders}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <PauseCircle className="w-4 h-4 text-gray-500" />
                      <span className="text-xs font-medium text-gray-700">Pausiert</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-700">{medPlanStats.pausedReminders}</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-purple-500" />
                      <span className="text-xs font-medium text-purple-800">Nutzer</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-700">{medPlanStats.usersWithReminders}</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <BarChart2 className="w-4 h-4 text-blue-500" />
                      <span className="text-xs font-medium text-blue-800">Ø pro Nutzer</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-700">{medPlanStats.avgRemindersPerUser}</p>
                  </div>
                </div>
                {medPlanStats.topMedications.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Häufigste Medikamente</h4>
                    <div className="space-y-2">
                      {medPlanStats.topMedications.map((med, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700 capitalize">{med.name}</span>
                          <span className="text-gray-500">{med.count}x</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Keine Med-Plan Daten verfügbar</p>
            )}
          </div>

          {/* Sleep Plan & Sleep Sounds Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Sleep Plan */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Moon className="w-5 h-5 text-indigo-500" />
                <h3 className="font-semibold text-gray-900">Schlafplan</h3>
              </div>
              {sleepPlanStats ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-indigo-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <PlayCircle className="w-4 h-4 text-indigo-500" />
                      <span className="text-xs font-medium text-indigo-800">Aktive Pläne</span>
                    </div>
                    <p className="text-2xl font-bold text-indigo-700">{sleepPlanStats.totalActivePlans}</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-purple-500" />
                      <span className="text-xs font-medium text-purple-800">Nutzer</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-700">{sleepPlanStats.usersWithPlans}</p>
                  </div>
                  {sleepPlanStats.avgPrematureWeeks > 0 && (
                    <div className="col-span-2 p-4 bg-amber-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-amber-500" />
                        <span className="text-xs font-medium text-amber-800">Ø Frühgeburtswochen</span>
                      </div>
                      <p className="text-2xl font-bold text-amber-700">{sleepPlanStats.avgPrematureWeeks}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Keine Schlafplan-Daten verfügbar</p>
              )}
            </div>

            {/* Sleep Sounds */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Volume2 className="w-5 h-5 text-teal-500" />
                <h3 className="font-semibold text-gray-900">Sleeping Sounds</h3>
              </div>
              {sleepSoundsStats ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-teal-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <PlayCircle className="w-4 h-4 text-teal-500" />
                      <span className="text-xs font-medium text-teal-800">Sessions</span>
                    </div>
                    <p className="text-2xl font-bold text-teal-700">{sleepSoundsStats.totalSessions}</p>
                  </div>
                  <div className="p-4 bg-teal-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-teal-500" />
                      <span className="text-xs font-medium text-teal-800">Spielzeit</span>
                    </div>
                    <p className="text-2xl font-bold text-teal-700">
                      {sleepSoundsStats.totalPlayMinutes >= 60
                        ? `${Math.round(sleepSoundsStats.totalPlayMinutes / 60)}h`
                        : `${sleepSoundsStats.totalPlayMinutes}m`}
                    </p>
                  </div>
                  <div className="col-span-2 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Volume2 className="w-4 h-4 text-gray-500" />
                      <span className="text-xs font-medium text-gray-700">Aktive Sounds</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-700">{sleepSoundsStats.activeSounds}</p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Keine Sleeping Sounds Daten verfügbar</p>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
